import {
  ButtonHTMLAttributes,
  ElementType,
  forwardRef,
  HTMLAttributes,
} from 'react';
import { Slot } from '@radix-ui/react-slot';
import { type VariantProps } from 'class-variance-authority';
import { cn } from '@shared/lib';
import { buttonVariants } from './buttonVariants';

type IconProps = {
  Icon: ElementType;
  iconPlacement: 'left' | 'right';
};

type IconRefProps = {
  Icon?: never;
  iconPlacement?: undefined;
};

export type ButtonProps = {
  asChild?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

export type ButtonIconProps = IconProps | IconRefProps;

const Button = forwardRef<HTMLButtonElement, ButtonProps & ButtonIconProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      Icon,
      iconPlacement,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {Icon && iconPlacement === 'left' && (
          <div className="group-hover:translate-x-100 w-0 translate-x-[0%] pr-0 opacity-0 transition-all duration-200 group-hover:w-5 group-hover:pr-2 group-hover:opacity-100">
            <Icon />
          </div>
        )}
        {props.children}
        {Icon && iconPlacement === 'right' && (
          <div className="w-0 translate-x-[100%] pl-0 opacity-0 transition-all duration-200 group-hover:w-5 group-hover:translate-x-0 group-hover:pl-2 group-hover:opacity-100">
            <Icon />
          </div>
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

type ButtonSpanProps = {
  asChild?: boolean;
} & HTMLAttributes<HTMLSpanElement> &
  VariantProps<typeof buttonVariants>;

//Usage: Use when you need a button in a deeply nested component that doesnt allow buttons
const ButtonSpan = forwardRef<HTMLSpanElement, ButtonSpanProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'span';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
ButtonSpan.displayName = 'Button';

export { Button, ButtonSpan };
